import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"

/**
 * Check whether a filter option is allowed for the current tenant role / plan.
 *
 * Option shape:
 * {
 *   value: string,
 *   requiredPermissions?: Array<{
 *     categoryId: number,
 *     permissionId: string,
 *     subPermissionId?: string,
 *   }>,
 *   requireAll?: boolean, // default: any permission is enough
 *   requiredIntegrationCodes?: string[], // any matching plan integration code
 * }
 *
 * Empty value ("All") is always allowed.
 */
export const isFilterOptionAllowed = (
  option,
  userRole,
  accountType,
  accountScope,
  userIntegrations
) => {
  if (!option || option.value === "" || option.value == null) return true

  const requiredCodes = option.requiredIntegrationCodes
  if (Array.isArray(requiredCodes) && requiredCodes.length > 0) {
    const codes = new Set(
      (Array.isArray(userIntegrations) ? userIntegrations : [])
        .map(int =>
          typeof int?.code === "string" ? int.code.trim().toLowerCase() : ""
        )
        .filter(Boolean)
    )
    const hasIntegration = requiredCodes.some(
      code => typeof code === "string" && codes.has(code.trim().toLowerCase())
    )
    if (!hasIntegration) return false
  }

  const required = option.requiredPermissions
  if (!Array.isArray(required) || required.length === 0) {
    // Integration-only options are fully decided above.
    return true
  }

  const results = required.map(rule => {
    if (!rule?.categoryId || !rule?.permissionId) return false
    if (rule.subPermissionId) {
      const subs = getSubPermissions(
        userRole,
        rule.categoryId,
        rule.permissionId,
        accountType,
        accountScope
      )
      return Boolean(subs?.[rule.subPermissionId]?.isEnabled)
    }
    return isPermissionEnabled(
      userRole,
      rule.categoryId,
      rule.permissionId,
      accountType,
      accountScope
    )
  })

  return option.requireAll ? results.every(Boolean) : results.some(Boolean)
}

export const filterOptionsByPermissions = (
  options,
  userRole,
  accountType,
  accountScope,
  userIntegrations
) =>
  (Array.isArray(options) ? options : []).filter(option =>
    isFilterOptionAllowed(
      option,
      userRole,
      accountType,
      accountScope,
      userIntegrations
    )
  )
